import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import './UserChatHistory.css';
import MarkdownRenderer from '../../utils/MarkdownRenderer';
import '../../utils/MarkDownRenderer.css';

const UserChatHistory = () => {
  const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';
  const { userId } = useParams(); // <-- grabs ":userId" from URL
  console.log("User id", userId);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userChats, setUserChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showChatMessages, setShowChatMessages] = useState(false);

  // Get token safely
  const getToken = useCallback(() => {
    try {
      return typeof Storage !== 'undefined' ? localStorage.getItem('token') : null;
    } catch (error) {
      console.warn('localStorage not available:', error);
      return null;
    }
  }, []);

  // Simple notification function
  const showErrorNotification = (message) => {
    console.error('User notification:', message);
  };

  const showSuccessNotification = (message) => {
    console.log('Success notification:', message);
  };

  // Fetch user details first
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      // const token = getToken();
      // if (!token) {
      //   setError('Authentication token not found');
      //   setLoading(false);
      //   return;
      // }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/getuser/${userId}`, {
          method: 'GET',
          credentials: 'include',
          // headers: {
          //   'Content-Type': 'application/json',
          //   'Authorization': `Bearer ${token}`
          // }
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Unauthorized access. Please login again.');
          }
          if (response.status === 404) {
            throw new Error('User not found');
          }
          throw new Error(`Failed to fetch user details: ${response.statusText}`);
        }

        const userData = await response.json();
        console.log(userData, 'userdata');

        if (!userData || typeof userData !== 'object') {
          throw new Error('Invalid user data received');
        }

        setUser(userData);

        // Automatically fetch chats after getting user details
        await fetchUserChats(userData);

      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(err.message);
        showErrorNotification(`Failed to load user details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, API_BASE_URL]);

  // Fetch user chats using the dashboard API
  const fetchUserChats = async (userData) => {
    setLoadingChats(true);
    setChatError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/chats`, {
        method: 'GET',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'Authorization': `Bearer ${token}`
        // }
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please login again.');
        }
        if (response.status === 404) {
          throw new Error('No chats found for this user');
        }
        throw new Error(`Failed to fetch chats: ${response.statusText}`);
      }

      const chatsData = await response.json();
      console.log('Chats data received:', chatsData);

      // The API returns an array of chats directly
      setUserChats(Array.isArray(chatsData) ? chatsData : []);

    } catch (chatFetchError) {
      console.error('Error fetching chats:', chatFetchError);
      setChatError(chatFetchError.message);
      showErrorNotification(`Failed to fetch chats: ${chatFetchError.message}`);
    } finally {
      setLoadingChats(false);
    }
  };

  // Fetch messages for a specific chat
  const fetchChatMessages = async (chatId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/chats/${chatId}/messages`, {
        method: 'GET',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'Authorization': `Bearer ${token}`
        // }
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`);
      }

      const messagesData = await response.json();
      console.log('Messages data received:', messagesData);

      return Array.isArray(messagesData) ? messagesData : [];

    } catch (error) {
      console.error('Error fetching chat messages:', error);
      showErrorNotification(`Failed to fetch messages: ${error.message}`);
      return [];
    }
  };

  // Handle viewing individual chat messages
  const handleViewChatMessages = async (chat) => {
    setSelectedChat(chat);
    setShowChatMessages(true);

    // Fetch detailed messages for this chat
    // const token = getToken();
    if (chat._id) {
      const messages = await fetchChatMessages(chat._id);
      setSelectedChat(prevChat => ({
        ...prevChat,
        messages: messages
      }));
    }
  };

  // Handle back to chat list
  const handleBackToChatList = () => {
    setSelectedChat(null);
    setShowChatMessages(false);
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Handle retry loading chats
  const handleRetryChats = async () => {
    if (user) {
      // const token = getToken();
      await fetchUserChats(user);
    }
  }


  // Format date safely with time
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="user-chat-history-container">
        <div className="loader-container">
          <div className="spinner"></div>
          <p>Loading user chat history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-chat-history-container">
        <div className="error-message">
          <h3>Unable to Load User Chat History</h3>
          <p>{error}</p>
          <button className="back-button" onClick={handleBackToDashboard}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-chat-history-container">
        <div className="error-message">
          <h3>User Not Found</h3>
          <p>The requested user could not be found.</p>
          <button className="back-button" onClick={handleBackToDashboard}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-chat-history-container">
      <div className="chat-history-header">
        <button className="back-button" onClick={handleBackToDashboard}>
          ← Back to Dashboard
        </button>
        <div className="user-info">
          <h2>Chat History for {user.name || user.email}</h2>
          <p className="user-details">
            User ID: #{user.id || user._id} • {user.email} •
            <button
              onClick={() => navigate(`/dashboard/user/${userId}/details`)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                textDecoration: 'underline',
                cursor: 'pointer',
                marginLeft: '8px',
                padding: 0,
                fontSize: 'inherit'
              }}
            >
              View User Details
            </button>
          </p>
        </div>
      </div>

      <div className="chat-history-content">
        {loadingChats ? (
          <div className="chats-loading">
            <div className="spinner"></div>
            <p>Loading chats...</p>
          </div>
        ) : chatError ? (
          <div className="chats-error">
            <h3>Error Loading Chats</h3>
            <p>{chatError}</p>
            <button onClick={handleRetryChats} className="retry-button">
              🔄 Retry
            </button>
          </div>
        ) : userChats.length === 0 ? (
          <div className="no-chats">
            <h3>No Chat History Found</h3>
            <p>This user hasn't started any conversations yet.</p>
          </div>
        ) : !showChatMessages ? (
          <div className="user-chats-list">
            <div className="user-chats-list-header">
              <h3>All Conversations ({userChats.length})</h3>
            </div>
            {userChats.map((chat, index) => (
              <div key={chat._id || chat.id || index} className="chat-item">
                <div className="chat-header">
                  <span className="chat-title">
                    {chat.title || `Chat ${index + 1}`}
                  </span>
                  <span className="chat-date">
                    {formatDate(chat.createdAt || chat.timestamp)}
                  </span>
                </div>
                <div className="chat-preview">
                  {chat.messages && chat.messages.length > 0 ? (
                    <p className="last-message">
                      <strong>Last message:</strong> {
                        chat.messages[chat.messages.length - 1].content?.substring(0, 150) ||
                        chat.messages[chat.messages.length - 1].text?.substring(0, 150) ||
                        'No content'
                      }{(chat.messages[chat.messages.length - 1].content?.length > 150 ||
                        chat.messages[chat.messages.length - 1].text?.length > 150) ? '...' : ''}
                    </p>
                  ) : (
                    <p className="no-messages">No messages in this chat</p>
                  )}
                  <div className="chat-stats">
                    <span className="message-count">
                      💬 {chat.messages ? chat.messages.length : 0} messages
                    </span>
                    {chat.status && (
                      <span className={`chat-status ${chat.status.toLowerCase()}`}>
                        {chat.status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="user-chat-actions">
                  <button
                    className="view-chat-button"
                    onClick={() => handleViewChatMessages(chat)}
                  >
                    👁️ View Conversation
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Chat Messages View (Dashboard-like interface)
          <div className="chat-messages-view">
            <div className="chat-messages-header">
              <button className="back-to-chats-button" onClick={handleBackToChatList}>
                ← Back to Chat List
              </button>
              <h4>{selectedChat?.title || 'Conversation'}</h4>
              <span className="chat-info">
                {selectedChat?.messages?.length || 0} messages • {formatDate(selectedChat?.createdAt)}
              </span>
            </div>

            <div className="chat-messages-container">
              {selectedChat?.messages && selectedChat.messages.length > 0 ? (
                selectedChat.messages.map((message, index) => (
                  <div
                    key={index}
                    className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
                  >
                    <div className="message-header">
                      <span className="message-role">
                        {message.role === 'user' ? '👤 User' : '🤖 Assistant'}
                      </span>
                      <span className="message-time">
                        {message.timestamp ? formatDate(message.timestamp) : ''}
                      </span>
                    </div>
                    <div className="user-message-content">
                      {message.role === 'assistant' ? (
                        <MarkdownRenderer text={message.content || message.text || 'No content'} />
                      ) : (
                        message.content || message.text || 'No content'
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-messages-in-chat">
                  <p>No messages in this conversation.</p>
                </div>
              )}
            </div>

            <div className="chat-readonly-notice">
              <p>📖 This is a read-only view of the user's chat history.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserChatHistory;